import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { formatDate, handleApiError, getImageUrl, showToast, showConfirm } from '../../utils/helpers';
import FolderFormModal from '../media/FolderFormModal';
import MediaPreviewModal from '../media/MediaPreviewModal';
import BulkPostCreatorModal from '../media/BulkPostCreatorModal';

const ClientMediaSection = ({ clientId, clientName }) => {
  const { isRTL } = useLanguage();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(new Set());
  const [showBulkCreator, setShowBulkCreator] = useState(false);

  const fetchData = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const [mediaRes, foldersRes] = await Promise.all([
        mediaAPI.getAll({
          client_id: clientId,
          folder_id: currentFolderId || 'root',
          page: 1,
          limit: 100,
        }),
        mediaAPI.getAllFolders({
          client_id: clientId,
          parent_id: currentFolderId || 'root',
        }),
      ]);
      setMedia(mediaRes.data.media || []);
      setFolders(foldersRes.data.folders || []);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientId, currentFolderId]);

  const handleFileSelect = async (files) => {
    if (!files?.length) return;
    const valid = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/mov', 'video/webm'].includes(f.type)
    );
    if (!valid.length) {
      showToast.error(isRTL ? 'أنواع ملفات غير مدعومة' : 'Unsupported file types');
      return;
    }
    try {
      setUploading(true);
      await mediaAPI.upload(valid, currentFolderId, clientId);
      await fetchData();
      showToast.success(isRTL ? `تم رفع ${valid.length} ملف` : `${valid.length} file(s) uploaded`);
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath((prev) => [...prev, folder]);
    setSelectedMedia(new Set());
  };

  const toggleMediaSelection = (id) => {
    setSelectedMedia((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkCreateSuccess = () => {
    setShowBulkCreator(false);
    setSelectedMedia(new Set());
    fetchData();
    showToast.success(isRTL ? 'تم إنشاء البوستات' : 'Posts created');
  };

  const handleBreadcrumbClick = (index) => {
    setSelectedMedia(new Set());
    if (index === -1) {
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setCurrentFolderId(newPath[newPath.length - 1].id);
      setFolderPath(newPath);
    }
  };

  const handleCreateFolder = () => {
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleDeleteFolder = async (folderId) => {
    const ok = await showConfirm(isRTL ? 'حذف هذا المجلد؟' : 'Delete this folder?', isRTL);
    if (!ok) return;
    try {
      await mediaAPI.deleteFolder(folderId);
      await fetchData();
      if (currentFolderId === folderId) {
        setCurrentFolderId(null);
        setFolderPath([]);
      }
      showToast.success(isRTL ? 'تم حذف المجلد' : 'Folder deleted');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    const ok = await showConfirm(isRTL ? 'حذف هذا الملف؟' : 'Delete this file?', isRTL);
    if (!ok) return;
    try {
      await mediaAPI.delete(mediaId);
      await fetchData();
      if (previewMedia?.id === mediaId) setPreviewMedia(null);
      showToast.success(isRTL ? 'تم الحذف' : 'Deleted');
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) handleFileSelect(e.dataTransfer.files);
  };

  if (!clientId) return null;

  return (
    <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-sky-100 text-sky-600 text-sm">📁</span>
          {isRTL ? `مكتبة الوسائط - ${clientName || 'العميل'}` : `Media Library - ${clientName || 'Client'}`}
        </h3>
        <div className="flex flex-wrap gap-2">
          {selectedMedia.size > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowBulkCreator(true)}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {isRTL ? `إنشاء بوستات (${selectedMedia.size})` : `Create Posts (${selectedMedia.size})`}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCreateFolder} className="border-slate-200">
            📁 {isRTL ? 'مجلد جديد' : 'New Folder'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => folderInputRef.current?.click()}
            disabled={uploading}
            className="border-slate-200"
          >
            📂 {isRTL ? 'رفع مجلد' : 'Upload Folder'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            loading={uploading}
          >
            + {isRTL ? 'رفع ملفات' : 'Upload Files'}
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={(e) => {
          if (e.target.files?.length) handleFileSelect(e.target.files);
          e.target.value = '';
        }}
        className="hidden"
      />
      <input
        ref={folderInputRef}
        type="file"
        webkitdirectory=""
        directory=""
        multiple
        onChange={(e) => {
          if (e.target.files?.length) handleFileSelect(e.target.files);
          e.target.value = '';
        }}
        className="hidden"
      />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <button
          onClick={() => handleBreadcrumbClick(-1)}
          className="hover:text-sky-600 font-medium"
        >
          {isRTL ? 'الرئيسية' : 'Home'}
        </button>
        {folderPath.map((f, i) => (
          <React.Fragment key={f.id}>
            <span>/</span>
            <button onClick={() => handleBreadcrumbClick(i)} className="hover:text-sky-600 font-medium truncate max-w-[120px]">
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Drop zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`rounded-xl border-2 border-dashed p-4 transition-colors ${
          dragActive ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-slate-50/50 hover:border-slate-300'
        }`}
      >
        <p className="text-xs text-slate-500 text-center">
          {isRTL ? 'اسحب الملفات أو المجلدات هنا للرفع' : 'Drag files or folders here to upload'}
        </p>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500">{isRTL ? 'جاري التحميل...' : 'Loading...'}</div>
      ) : (
        <div className="space-y-6">
          {/* Folders - Google Drive style */}
          {folders.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-slate-500 uppercase mb-3">{isRTL ? 'المجلدات' : 'Folders'}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {folders.map((folder) => (
                  <Card
                    key={folder.id}
                    className="cursor-pointer hover:shadow-md transition-all group border-slate-200 p-0 overflow-hidden"
                    onClick={() => handleFolderClick(folder)}
                  >
                    <div className="aspect-square bg-gradient-to-br from-sky-50 to-emerald-50 flex items-center justify-center">
                      <span className="text-4xl">📁</span>
                    </div>
                    <div className="p-2">
                      <h4 className="font-medium text-slate-900 truncate text-sm" title={folder.name}>
                        {folder.name}
                      </h4>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
                        <span>{folder.media_count || 0} {isRTL ? 'ملف' : 'files'}</span>
                        {folder.subfolders_count > 0 && (
                          <span>• {folder.subfolders_count} {isRTL ? 'مجلد' : 'folders'}</span>
                        )}
                      </div>
                      <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditFolder(folder); }}
                          className="text-xs text-sky-600 hover:text-sky-700"
                        >
                          {isRTL ? 'تعديل' : 'Edit'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                          className="text-xs text-rose-600 hover:text-rose-700"
                        >
                          {isRTL ? 'حذف' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Media files */}
          <div>
            <h4 className="text-xs font-medium text-slate-500 uppercase mb-3">
              {isRTL ? 'الملفات' : 'Files'}
              {media.length > 0 && (
                <span className="font-normal normal-case text-slate-400 ml-2">
                  {isRTL ? '(اضغط المربع لاختيار، ثم إنشاء بوستات • اختر كاروسال لبوست واحد)' : '(click checkbox to select, then Create Posts • choose Carousel for one post)'}
                </span>
              )}
            </h4>
            {media.length === 0 && folders.length === 0 ? (
              <div className="py-12 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
                <p className="text-sm">{isRTL ? 'لا توجد ملفات أو مجلدات. ارفع ملفات أو أنشئ مجلداً.' : 'No files or folders. Upload files or create a folder.'}</p>
              </div>
            ) : media.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">{isRTL ? 'لا توجد ملفات في هذا المجلد' : 'No files in this folder'}</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {media.map((m) => {
                  const isSelected = selectedMedia.has(m.id);
                  return (
                  <Card
                    key={m.id}
                    className={`cursor-pointer hover:shadow-md transition-all group border-slate-200 p-0 overflow-hidden relative ${isSelected ? 'ring-2 ring-sky-500' : ''}`}
                    onClick={(e) => {
                      if (e.target.closest('button')) return;
                      if (e.shiftKey || e.ctrlKey || e.metaKey) {
                        toggleMediaSelection(m.id);
                      } else {
                        setPreviewMedia(m);
                      }
                    }}
                  >
                    {isSelected && (
                      <div className={`absolute top-1 z-10 w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center text-white text-xs ${isRTL ? 'left-1' : 'right-1'}`}>✓</div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleMediaSelection(m.id); }}
                      className={`absolute top-1 z-10 w-5 h-5 rounded border-2 border-slate-300 bg-white/90 flex items-center justify-center hover:border-sky-500 ${isRTL ? 'right-1' : 'left-1'}`}
                    >
                      {isSelected && <span className="text-sky-600 text-xs">✓</span>}
                    </button>
                    <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                      {m.file_type === 'video' ? (
                        <video src={getImageUrl(m.file_url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                      ) : (
                        <img src={getImageUrl(m.file_url)} alt={m.file_name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-slate-700 truncate" title={m.file_name}>{m.file_name}</p>
                      <p className="text-[10px] text-slate-400">{formatDate(m.created_at)}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMedia(m.id); }}
                        className="text-[10px] text-rose-600 hover:text-rose-700 mt-1 opacity-0 group-hover:opacity-100"
                      >
                        {isRTL ? 'حذف' : 'Delete'}
                      </button>
                    </div>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showFolderModal && (
        <FolderFormModal
          folder={editingFolder}
          parentId={currentFolderId}
          clientId={clientId}
          onClose={() => { setShowFolderModal(false); setEditingFolder(null); }}
          onSuccess={() => { fetchData(); setShowFolderModal(false); setEditingFolder(null); showToast.success(isRTL ? 'تم الحفظ' : 'Saved'); }}
        />
      )}

      {previewMedia && (
        <MediaPreviewModal
          media={previewMedia}
          onClose={() => setPreviewMedia(null)}
          onUpdate={(updated) => setMedia((prev) => prev.map((m) => (m.id === updated.id ? updated : m)))}
          onDelete={() => { handleDeleteMedia(previewMedia.id); setPreviewMedia(null); }}
        />
      )}

      {showBulkCreator && selectedMedia.size > 0 && (
        <BulkPostCreatorModal
          mediaIds={Array.from(selectedMedia)}
          initialClientId={clientId}
          onClose={() => setShowBulkCreator(false)}
          onSuccess={handleBulkCreateSuccess}
        />
      )}
    </div>
  );
};

export default ClientMediaSection;
